// src/pages/Products.tsx
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Package, ShoppingCart, Sparkles, Wallet } from 'lucide-react';
import { productsAPI, ordersAPI, walletAPI, ClientProduct, toSafeNumber } from '../services/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const fa = new Intl.NumberFormat('fa-IR');

const Products: React.FC = () => {
    const navigate = useNavigate();
    const [products, setProducts] = useState<ClientProduct[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [orderLoading, setOrderLoading] = useState<number | null>(null);
    const [qty, setQty] = useState<Record<number, number>>({});
    const [walletBalance, setWalletBalance] = useState<number>(0);

    useEffect(() => {
        (async () => {
            try {
                const [prods, wal] = await Promise.all([
                    productsAPI.getClientProducts(),
                    // ✅ تغییر نام متد
                    walletAPI.getWalletSummary().catch(() => ({ balance: 0 })),
                ]);
                setProducts(prods);
                setWalletBalance(Number(wal.balance || 0));
                const initQty: Record<number, number> = {};
                prods.forEach((p: ClientProduct) => (initQty[p.id] = 1));
                setQty(initQty);
            } catch (err: any) {
                setError(err?.message || 'خطا در دریافت اطلاعات');
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const changeQty = (productId: number, delta: number) => {
        setQty((prev) => {
            const current = prev[productId] ?? 1;
            return { ...prev, [productId]: Math.max(1, current + delta) };
        });
    };

    const handleCreateOrder = async (productId: number) => {
        const safeProductId = toSafeNumber(productId, 0);
        if (safeProductId <= 0) {
            toast.error('شناسه محصول نامعتبر است.');
            return;
        }

        const count = Math.max(1, toSafeNumber(qty[productId], 1));
        if (count <= 0) {
            toast.error('تعداد نامعتبر است.');
            return;
        }

        setOrderLoading(safeProductId);
        try {
            await ordersAPI.createOrder({ product_id: safeProductId, quantity: count });
            toast.success('سفارش با موفقیت ثبت شد.');
            navigate('/orders');
        } catch (err: any) {
            toast.error(err?.message || 'خطا در ثبت سفارش');
        } finally {
            setOrderLoading(null);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
                <Loader2 className="h-12 w-12 animate-spin text-green-600" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6">
                <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>
            </div>
        );
    }

    return (
        <div dir="rtl" className="space-y-6 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 min-h-screen p-4 md:p-8">
            {/* Header */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 p-6 md:p-8 shadow-2xl">
                <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                    <div className="text-white space-y-2">
                        <div className="flex items-center gap-3">
                            <Sparkles className="h-8 w-8" />
                            <h1 className="text-3xl md:text-4xl font-bold">محصولات</h1>
                        </div>
                        <p className="text-green-100">لیست محصولات قابل خرید</p>
                    </div>
                    <div className="bg-white/15 backdrop-blur-md rounded-2xl px-6 py-4 border border-white/30 min-w-[200px]">
                        <div className="flex items-center gap-2">
                            <Wallet className="h-6 w-6 text-emerald-100" />
                            <span className="text-sm text-emerald-50">موجودی کیف‌پول</span>
                        </div>
                        <div className="text-2xl font-bold text-white">
                            {fa.format(walletBalance / 10)} تومان
                        </div>
                    </div>
                </div>
            </div>

            {/* Products Grid */}
            {products.length === 0 ? (
                <Card><CardContent className="text-center py-12"><Package className="h-12 w-12 mx-auto mb-4 text-green-400" /><p>محصولی یافت نشد</p></CardContent></Card>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {products.map((product) => {
                        const count = qty[product.id] ?? 1;
                        const totalFinal = product.final_price * count;
                        const hasEnough = walletBalance >= totalFinal;

                        return (
                            <Card key={product.id} className="border-emerald-600/60 bg-white/85 backdrop-blur-sm hover:shadow-xl transition-all">
                                <CardHeader>
                                    <CardTitle className="text-green-900">{product.title}</CardTitle>
                                    <CardDescription>{product.description}</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="rounded-xl bg-green-50 p-3 space-y-2">
                                        <div className="flex justify-between">
                                            <span className="text-gray-700">قیمت پایه:</span>
                                            <span className="font-bold text-green-700">{fa.format(product.base_price / 10)} تومان</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-700">کمیسیون:</span>
                                            <span className="font-bold text-emerald-600">{fa.format(product.commission_amount / 10)} تومان</span>
                                        </div>
                                        <div className="flex justify-between border-t border-green-200 pt-2">
                                            <span className="text-gray-900 font-bold">قیمت نهایی:</span>
                                            <span className="text-green-700 font-bold text-lg">{fa.format(product.final_price / 10)} تومان</span>
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-700">تعداد:</span>
                                        <div className="inline-flex items-center rounded-lg border border-emerald-300 bg-white overflow-hidden">
                                            <button onClick={() => changeQty(product.id, -1)} disabled={count <= 1} className="h-10 w-10 text-emerald-700 hover:bg-emerald-50">−</button>
                                            <input type="number" min={1} value={count} onChange={(e) => setQty(prev => ({ ...prev, [product.id]: Math.max(1, Number(e.target.value)) }))} className="h-10 w-16 text-center border-x border-emerald-200" />
                                            <button onClick={() => changeQty(product.id, 1)} className="h-10 w-10 text-emerald-700 hover:bg-emerald-50">+</button>
                                        </div>
                                    </div>

                                    <div className="rounded-xl border p-3 bg-white">
                                        <div className="flex justify-between">
                                            <span className="text-gray-700">جمع کل:</span>
                                            <span className="font-bold text-emerald-700">{fa.format(totalFinal / 10)} تومان</span>
                                        </div>
                                    </div>

                                    {!hasEnough && (
                                        <div className="rounded-xl border-red-200 bg-red-50 p-2 text-sm text-red-700 text-center">
                                            موجودی کافی نیست. لطفاً کیف پول را شارژ کنید.
                                        </div>
                                    )}

                                    <Button onClick={() => handleCreateOrder(product.id)} disabled={orderLoading === product.id || !hasEnough} className="w-full bg-green-600 hover:bg-green-700 text-white">
                                        {orderLoading === product.id ? <Loader2 className="animate-spin h-4 w-4" /> : <><ShoppingCart className="ml-2 h-4 w-4" /> ثبت سفارش</>}
                                    </Button>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default Products;
